<template>
  <CCard class="bg-style2">
    <CCardBody>
      <GmapMap
        ref="gmap"
        :center="mapCenter"
        :zoom="14"
        style="height: 485px"
        :options="mapOptions"
      >
        <!-- Marker ของเหตุการณ์ทั้งหมด -->
        <GmapMarker
          v-if="showCases"
          v-for="(event, index) in caseMarkers"
          :key="'case-' + index"
          :position="event.position"
          :title="event.title"
          :icon="{
            url: require('@/assets/case.png'),
            scaledSize: { width: 40, height: 40 }
          }"
          :draggable="false"
          @click="handleToggleInfoWindow(event)"
        />

        <!-- Marker ของรถทั้งหมด -->
        <GmapMarker
          v-if="showCars"
          v-for="(car, index) in carMarkers"
          :key="'car-' + index"
          :position="car.position"
          :title="car.title"
          :icon="car.icon"
          :draggable="false"
          @click="handleToggleInfoWindow(car)"
        />

        <!-- InfoWindow -->
        <GmapInfoWindow
          :position="infoWindowPos"
          :opened="infoWinOpen"
          :options="infoOptions"
          @closeclick="closeInfoWindow"
        >
            <h6 class="info-title">{{ infoContent }}</h6>
        </GmapInfoWindow>

        <!-- Polyline สำหรับแสดงเส้นทาง (animated) -->
        <GmapPolyline
          v-if="displayPath && displayPath.length > 0"
          :path="displayPath"
          :options="polylineOptions"
        />

        <!-- Marker สำหรับแสดงเวลาโดยประมาณบนเส้นทาง -->
        <GmapMarker
          v-if="routeMidPoint && durationText"
          :position="routeMidPoint"
          :icon="durationMarkerIcon"
          :z-index="1000"
        />
      </GmapMap>
    </CCardBody>
  </CCard>
</template>

<script>
import * as VueGoogleMaps from 'vue2-google-maps';
import Vue from 'vue';
import { mapGetters } from 'vuex';

Vue.use(VueGoogleMaps, {
  load: {
    key: 'AIzaSyDBMns5PZsDXIfXsT1E1_79jx2934NTUHM',
  },
});

export default {
  name: 'GoogleMaps',
  data() {
    return {
      mapCenter: { lat: 20.045, lng: 99.893572 },
      infoWindowPos: null,
      infoContent: '',
      infoWinOpen: false,
      infoOptions: { pixelOffset: { width: 0, height: -35 } },
      mapOptions: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: true,
        rotateControl: false,
        fullscreenControl: true,
        disableDefaultUI: false,
      },
      animatedPath: [], // สำหรับเก็บ path ที่กำลัง animate
      animationProgress: 0, // 0-100 สำหรับ animation progress
      animationInterval: null,
    };
  },

  props: {
    selectedCaseId: {
      type: String,
      default: null
    },
    showCases: { type: Boolean, default: true },
    showCars: { type: Boolean, default: true }, // ควบคุมการแสดง marker ของรถ
    center: Object,
    route: {
      type: Object,
      default: null
    },
    filteredReports: {
      type: Array,
      default: null // ถ้าเป็น null จะใช้ reports จาก store
    },
  },
  
  computed: {
    ...mapGetters({
      reports: 'Dashboard/reports',
      cars: 'Car/cars',
    }),

    caseMarkers() {
      // ใช้ filteredReports ถ้ามี (จากหน้า index.vue) หรือใช้ reports จาก store (จากหน้า Dashboard2.vue)
      const reportsToUse = this.filteredReports !== null ? this.filteredReports : this.reports;
      
      if (!Array.isArray(reportsToUse)) return [];
      let markers = reportsToUse
        .filter(r => r.location?.coordinates?.length === 2)
        .map(r => {
          const [lng, lat] = r.location.coordinates;
          return {
            id: r._id,
            position: { lat, lng },
            title: r.title || r.location.address || 'เหตุการณ์',
          };
        });

      if (this.selectedCaseId) {
        markers = markers.filter(m => m.id === this.selectedCaseId);
      }

      return markers;
    },

    carMarkers() {
      if (this.selectedCaseId && Array.isArray(this.reports)) {
        const rep = this.reports.find(r => r._id === this.selectedCaseId);
        const carsInCase = rep?.cars || [];
        return carsInCase
          .filter(c => Array.isArray(c.coordinates) && c.coordinates.length === 2)
          .map(c => {
            const [lng, lat] = c.coordinates;
            const model = Array.isArray(c.title) ? (c.title.find(t => t.key === 'th')?.value || c.title[0]?.value) : (c.title || '');
            const plate =
              Array.isArray(c.description)
                ? (c.description.find(d => d.key === 'plate')?.value || c.description[0]?.value || '')
                : (c.description || '');
            const title = plate ? `${model || 'รถ'} : ${plate}` : (model || 'รถ');
            let iconUrl = require('@/assets/cartopview.png');
            try {
              const typeArr = c.type || [];
              const th = (typeArr.find(t => t.key === 'th') || {}).value || '';
              const en = (typeArr.find(t => t.key === 'en') || {}).value || '';
              if (/พยาบาล/i.test(th) || /ambulance/i.test(en)) {
                iconUrl = require('@/assets/ambulancetopview.png');
              }
            } catch (e) {
              // fallback to default cartopview
            }
            return { position: { lat, lng }, title, icon: { url: iconUrl, scaledSize: { width: 60, height: 60 } } };
          });
      }

      if (!Array.isArray(this.cars)) return [];
      return this.cars
        .filter(c => c.coordinates?.length === 2)
        .map(c => {
          const [lng, lat] = c.coordinates;
          const model = Array.isArray(c.title) ? (c.title.find(t => t.key === 'th')?.value || c.title[0]?.value) : (c.title || '');
          const plate =
            Array.isArray(c.description)
              ? (c.description.find(d => d.key === 'plate')?.value || c.description[0]?.value || '')
              : (c.description || '');
          const title = plate ? `${model || 'รถ'} : ${plate}` : (model || 'รถ');
          let iconUrl = require('@/assets/cartopview.png');
          try {
            const typeArr = c.type || [];
            const th = (typeArr.find(t => t.key === 'th') || {}).value || '';
            const en = (typeArr.find(t => t.key === 'en') || {}).value || '';
            if (/พยาบาล/i.test(th) || /ambulance/i.test(en)) {
              iconUrl = require('@/assets/ambulancetopview.png');
            }
          } catch (e) {
            // ignore and use default
          }
          return { position: { lat, lng }, title, icon: { url: iconUrl, scaledSize: { width: 60, height: 60 } } };
        });
    },

    polylineOptions() {
      const options = {
        strokeColor: '#0000FF', // สีน้ำเงิน
        strokeOpacity: 0.8,
        strokeWeight: 8,
        geodesic: true,
        clickable: false,
      };
      return options;
    },

    // Path ที่ใช้แสดง (animated path)
    displayPath() {
      if (this.animatedPath.length > 0) {
        return this.animatedPath;
      }
      if (this.route && this.route.path && this.route.path.length > 0) {
        return this.route.path;
      }
      return [];
    },

    // หาจุดกลางของเส้นทางสำหรับแสดงเวลา
    routeMidPoint() {
      if (!this.route || !this.route.path || this.route.path.length === 0) {
        return null;
      }
      const midIndex = Math.floor(this.route.path.length / 2);
      return this.route.path[midIndex];
    },

    // แปลงเวลาเป็นข้อความ
    durationText() {
      if (!this.route || !this.route.duration) {
        return '';
      }
      const seconds = this.route.duration;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return `${hours} ชม. ${minutes} นาที`;
      }
      
      if (minutes > 0) {
        return `${minutes} นาที`;
      }
      return '1 นาที';
    },

    // Icon สำหรับ marker ที่แสดงเวลา
    durationMarkerIcon() {
  if (!this.durationText) {
    return null;
  }
  
  const text = this.durationText;
  // กำหนดความกว้างพื้นฐาน (ประมาณ 8px ต่อตัวอักษร)
  // '15 นาที' ยาวกว่า '1 ชม. 30 นาที' เล็กน้อย
  const baseWidth = 90; 
  let width = baseWidth;

  // ปรับความกว้างตามความยาวของข้อความคร่าวๆ 
  // ตัวอย่างเช่น: '15 นาที' (6 ตัวอักษร) ได้ 90px
  // '1 ชม. 30 นาที' (11 ตัวอักษร) ได้ 120px
  // '2 ชม. 15 นาที' (11 ตัวอักษร) ได้ 120px
  if (text.length > 8) {
    width = 120; // สำหรับข้อความยาว เช่น มีชั่วโมงด้วย
  }
  if (text.length > 11) {
    width = 140; 
  }

  const height = 35;
  const radius = 17; // ทำให้เป็น pill shape

  const svg = `
        <svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'>
          <defs>
            <filter id="shadow" x="0" y="0" width="200%" height="200%">
              <feOffset result="offOut" in="SourceAlpha" dx="0" dy="1" />
              <feGaussianBlur result="blurOut" in="offOut" stdDeviation="1" />
              <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
            </filter>
          </defs>
          
          <rect width='${width}' height='${height}' rx='${radius}' fill='#1976D2' filter="url(#shadow)"/>
          
          <text x='${width / 2}' y='23' font-family='Kanit, Arial' font-size='14' font-weight='600' fill='white' text-anchor='middle'>
            ${text}
          </text>
        </svg>
      `;
      
      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
        scaledSize: { width, height },
        // กำหนดจุด anchor ให้อยู่ที่กึ่งกลางด้านล่างของ Marker
        anchor: { x: width / 2, y: height }
      };
    },
  },
  
  watch: {
    reports: { handler: 'updateMapCenter', immediate: true },
    cars: { handler: 'updateMapCenter', immediate: true },
    center: {
      handler(newVal) {
        if (newVal) {
          this.mapCenter = newVal;
          this.$nextTick(() => {
            try {
              const mapComp = this.$refs.gmap;
              const mapObj = mapComp && mapComp.$mapObject;
              if (mapObj && newVal.lat != null && newVal.lng != null) {
                mapObj.panTo({ lat: newVal.lat, lng: newVal.lng });
                if (newVal.zoom) mapObj.setZoom(newVal.zoom);
              }
            } catch (e) {
              // ignore if map not ready
            }
          });
        }
      },
      deep: true,
      immediate: true
    },
    route: {
      handler(newRoute) {
        if (newRoute && newRoute.path && newRoute.path.length > 0) {
          // เริ่ม animation เมื่อมี route ใหม่
          this.startPathAnimation(newRoute.path);
        } else {
          // ล้าง animation เมื่อไม่มี route
          this.stopPathAnimation();
          this.animatedPath = [];
        }
      },
      immediate: true
    },
  },
  
  methods: {
    updateMapCenter() {
      if (this.caseMarkers.length > 0) {
        this.mapCenter = this.caseMarkers[0].position;
      } else if (this.carMarkers.length > 0) {
        this.mapCenter = this.carMarkers[0].position;
      } else if (this.center) {
        this.mapCenter = this.center;
      }
    },
    
    handleToggleInfoWindow(marker) {
      this.infoWindowPos = marker.position;
      this.infoContent = marker.title;
      this.infoWinOpen = true;
    },
    
    closeInfoWindow() {
      this.infoWinOpen = false;
    },

    // 👇 เพิ่ม method ใหม่สำหรับเปิด InfoWindow จากภายนอก
    showInfoWindowAt(position, title) {
      this.infoWindowPos = position;
      this.infoContent = title;
      this.infoWinOpen = true;
    },

    // เริ่ม animation สำหรับเส้นทาง
    startPathAnimation(fullPath) {
      // หยุด animation เดิมถ้ามี
      this.stopPathAnimation();
      
      if (!fullPath || fullPath.length === 0) {
        this.animatedPath = [];
        return;
      }

      // เริ่มต้นด้วย path ว่าง
      this.animatedPath = [];
      this.animationProgress = 0;

      // คำนวณจำนวนจุดที่จะแสดงต่อ frame
      const totalPoints = fullPath.length;
      const animationDuration = 1000; // 2 วินาที
      const frameRate = 60; // 60 FPS
      const frames = (animationDuration / 1000) * frameRate;
      const pointsPerFrame = Math.max(1, Math.ceil(totalPoints / frames));

      let currentIndex = 0;

      this.animationInterval = setInterval(() => {
        currentIndex += pointsPerFrame;
        
        if (currentIndex >= totalPoints) {
          // Animation เสร็จแล้ว - แสดงเส้นทางทั้งหมด
          this.animatedPath = [...fullPath];
          this.animationProgress = 100;
          this.stopPathAnimation();
        } else {
          // แสดงเส้นทางทีละส่วน
          this.animatedPath = fullPath.slice(0, currentIndex);
          this.animationProgress = (currentIndex / totalPoints) * 100;
        }
      }, 1000 / frameRate);
    },

    // หยุด animation
    stopPathAnimation() {
      if (this.animationInterval) {
        clearInterval(this.animationInterval);
        this.animationInterval = null;
      }
    },
  },

  beforeDestroy() {
    // ทำความสะอาด animation เมื่อ component ถูก destroy
    this.stopPathAnimation();
  },

  calculateDrivingDistance() {
    if (!window.google || !window.google.maps) {
      console.error("Google Maps API ยังไม่โหลด");
      return;
    }
    if (!this.carMarkers.length) {
      console.warn("ไม่มีข้อมูลรถในแผนที่");
      return;
    }

    const service = new google.maps.DistanceMatrixService();

    const origins = [this.mapCenter];
    const destinations = this.carMarkers.map(c => c.position);

    service.getDistanceMatrix(
      {
        origins,
        destinations,
        travelMode: 'DRIVING',
        unitSystem: google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status !== 'OK') {
          console.error('DistanceMatrix error:', status);
          return;
        }

        const results = response.rows[0].elements;
        results.forEach((res, i) => {
          const car = this.carMarkers[i];
          if (res.status === 'OK') {
            const distance = res.distance.text;
            const duration = res.duration.text;
            console.log(`${car.title} → ระยะทาง: ${distance}, เวลาโดยประมาณ: ${duration}`);
          } else {
            console.warn(`ไม่สามารถคำนวณระยะทางของ ${car.title}: ${res.status}`);
          }
        });
      }
    );
  },
  
  created() {
    if (!this.cars || this.cars.length === 0) {
      this.$store.dispatch('Car/cars');
    }
  },
};
</script>

